import { useCallback, useEffect, useRef, useState } from 'react';

/** Tolerance en px : absorbe les arrondis sub-pixel en bout de course. */
const EPSILON = 2;

export interface ScrollShadows {
  /** A poser sur le conteneur qui defile (overflow-y). */
  scrollRef: React.RefObject<HTMLDivElement | null>;
  /** A poser sur l'enveloppe DIRECTE du contenu (mesure sa hauteur reelle). */
  contentRef: React.RefObject<HTMLDivElement | null>;
  /** Vrai s'il reste du contenu AU-DESSUS (donc : fondre le bord haut). */
  hasAbove: boolean;
  /** Vrai s'il reste du contenu EN DESSOUS (donc : fondre le bord bas). */
  hasBelow: boolean;
}

/**
 * Ombres de defilement : dit s'il reste du contenu au-dessus / en dessous du
 * cadre visible. L'appelant pose les classes .taki-scroll-mask-* de @taki/ui
 * sur le conteneur qui defile : le bord du cadre efface alors le contenu
 * (mask-image), rien n'est peint par-dessus.
 *
 * Ecoute le scroll (passif) et observe deux boites : le cadre (il change de
 * taille au redimensionnement) et le contenu (il change de hauteur a chaque
 * navigation d'ecran, sans que le cadre bouge).
 */
export function useScrollShadows(): ScrollShadows {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState({ hasAbove: false, hasBelow: false });

  const measure = useCallback((): void => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    setState({
      hasAbove: scrollTop > EPSILON,
      hasBelow: scrollTop + clientHeight < scrollHeight - EPSILON,
    });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    measure();
    el.addEventListener('scroll', measure, { passive: true });

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    if (contentRef.current) observer.observe(contentRef.current);

    return () => {
      el.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, [measure]);

  return { scrollRef, contentRef, ...state };
}
